import React, { useEffect, useState, Suspense, lazy } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { SEO } from '../components/shared/SEO';
import { Header } from '../components/layout/Header';
import { AnnouncementBar } from '../components/layout/AnnouncementBar';
import { MobileDeliverToPill } from '../components/layout/MobileDeliverToPill';
import { Footer } from '../components/layout/Footer';
import { MobileFooterNav } from '../components/layout/MobileFooterNav';
import { PharmacistChat } from '../components/services/PharmacistChat';
import { MobileHomeHeader } from '../components/MobileHomeHeader';
import { MobileInternalHeader } from '../components/layout/MobileInternalHeader';
import { LocationModal } from '../components/modals/LocationModal';
import { CouponModal } from '../components/modals/CouponModal';
import { SectionSkeleton } from '../components/shared/SkeletonLoader';
import { TicketIcon } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { OffersHeroBanner } from '../components/offers/OffersHeroBanner';
import { TopOffers } from '../components/home/TopOffers';
import { ShopByCategory } from '../components/home/ShopByCategory';
import { OffersBannerRow } from '../components/offers/OffersBannerRow';
import { OffersFullWidthBanner } from '../components/offers/OffersFullWidthBanner';
import { PageHeroBreadcrumb } from '../components/layout/PageHeroBreadcrumb';
import { PullToRefresh } from '../components/shared/PullToRefresh';
const FlashSales = lazy(() => import('../components/home/FlashSales').then(m => ({
  default: m.FlashSales
})));
const DealOfTheDay = lazy(() => import('../components/home/DealOfTheDay').then(m => ({
  default: m.DealOfTheDay
})));
const TrendingProducts = lazy(() => import('../components/home/TrendingProducts').then(m => ({
  default: m.TrendingProducts
})));
const ExclusiveDeals = lazy(() => import('../components/home/ExclusiveDeals').then(m => ({
  default: m.ExclusiveDeals
})));
const EnhancedClearanceSection = lazy(() => import('../components/home/EnhancedClearanceSection').then(m => ({
  default: m.EnhancedClearanceSection
})));
const tabs: {
  id: string;
  label: string;
  sectionId?: string;
}[] = [{
  id: 'all',
  label: 'All'
}, {
  id: 'flash',
  label: '⚡ Flash',
  sectionId: 'flash-sales'
}, {
  id: 'deal-of-day',
  label: '🔥 Deal of Day',
  sectionId: 'deal-of-day'
}, {
  id: 'trending',
  label: '📈 Trending',
  sectionId: 'trending-now'
}, {
  id: 'exclusive',
  label: '💎 Exclusive',
  sectionId: 'exclusive-deals'
}, {
  id: 'clearance',
  label: '🏷️ Clearance',
  sectionId: 'clearance-sale'
}];
export function OffersPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNowContext = searchParams.get('from') === 'now';
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string;
  } | null>(null);
  const [activeTab, setActiveTab] = useState('all');
  const [couponModalOpen, setCouponModalOpen] = useState(false);
  const handleTabClick = (tab: (typeof tabs)[number]) => {
    setActiveTab(tab.id);
    if (tab.sectionId) {
      const el = document.getElementById(tab.sectionId);
      if (el) {
        const offset = window.innerWidth >= 768 ? 240 : 170;
        const top = el.getBoundingClientRect().top + window.pageYOffset - offset;
        window.scrollTo({
          top,
          behavior: 'smooth'
        });
        // brief highlight
        el.classList.add('ring-2', 'ring-[#fdc300]', 'ring-offset-2', 'rounded-xl');
        setTimeout(() => {
          el.classList.remove('ring-2', 'ring-[#fdc300]', 'ring-offset-2', 'rounded-xl');
        }, 1500);
      }
    } else {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };
  // Update active tab based on which section is most visible
  useEffect(() => {
    const handler = () => {
      const sectionTabs = tabs.filter(t => t.sectionId);
      let current = 'all';
      const viewportMid = window.innerHeight / 2;
      for (const t of sectionTabs) {
        const el = document.getElementById(t.sectionId!);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.top <= viewportMid && rect.bottom >= viewportMid) {
          current = t.id;
          break;
        }
      }
      setActiveTab(current);
    };
    window.addEventListener('scroll', handler, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handler);
  }, []);
  return <HelmetProvider data-id="element-7446" data-mp="3d5vnj1mtg57x">
      <SEO title="Deals & Offers - Save Up to 70% on Health Products" description="Discover amazing deals and exclusive offers at Innova Pharmacy. Save up to 70% on health, beauty, and wellness products with fast delivery across Saudi Arabia." canonical="/en/offers/" robots="index, follow, max-image-preview:large" hreflangAr="/offers/" hreflangEn="/en/offers/" hreflangDefault="/offers/" ogImage="https://cdn.innovaksa.net/og/offers.jpg" keywords="pharmacy deals Saudi Arabia, health product offers, discount supplements, beauty sale, vitamins on sale, pharmacy promotions Riyadh, cheap medications, wellness deals" data-id="element-7447" data-mp="3d76o6skyswut" />
      <div className="flex flex-col min-h-screen bg-gray-50 overflow-x-hidden pb-32 md:pb-0" data-id="element-7448" data-mp="1isn81at9hg9q">
        {/* Desktop Header */}
        <div className="hidden md:block" data-id="element-7449" data-mp="1nn14zhnknvgi">
          <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} showAnnouncement={true} data-id="element-7450" data-mp="1rmrt1dlprxhc" />
        </div>
        {/* Mobile Header Overlay */}
        <div className="md:hidden fixed top-0 left-0 right-0 z-50" data-id="element-7451" data-mp="1j7zrfexv9d2">
          <MobileInternalHeader title="Deals & Offers" data-id="element-7452" data-mp="3sp9f405xsp4i" />
        </div>

        <PullToRefresh onRefresh={async () => {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }} data-id="element-7453" data-mp="b89hba14tyt5">
          <main className="flex-grow pt-14 md:pt-[var(--header-height-desktop)]" data-id="element-7454" data-mp="6grgclz3zy86">
            <h1 className="sr-only" data-id="element-7455" data-mp="k86bfr6ajpvl">
              Deals & Offers - Save Big on Health and Beauty Products
            </h1>
            <PageHeroBreadcrumb breadcrumbs={[{
            label: 'Home',
            href: '/'
          }, {
            label: 'Offers'
          }]} title="Deals & Offers" description="Discover amazing deals and exclusive offers at Innova Pharmacy. Save up to 70% on health, beauty, and wellness products with fast delivery across Saudi Arabia." data-id="element-7456" data-mp="g6t41cnjvemk" />

            {/* Sticky Mobile Tab Bar — scrolls to section + tracks active */}
            <div className="md:hidden sticky top-[56px] z-40 bg-white/95 backdrop-blur border-b border-gray-200 shadow-sm" data-id="element-7457" data-mp="1bgpou156kl2x">
              <div className="overflow-x-auto scrollbar-hide px-4 py-2.5" data-id="element-7458" data-mp="wqs7o55o4vn8">
                <div className="flex gap-2" data-id="element-7459" data-mp="11puzup0jv7ds">
                  {tabs.map(tab => <button key={tab.id} onClick={() => handleTabClick(tab)} className={`whitespace-nowrap px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${activeTab === tab.id ? 'bg-[#1E2B55] text-white shadow-sm' : 'bg-gray-100 text-gray-700 active:bg-gray-200'}`} data-id="element-7460" data-mp="nqc6x3gb82m2">
                      {tab.label}
                    </button>)}
                </div>
              </div>
            </div>

            <section className="py-2 md:py-6" data-id="element-7461" data-mp="2d61ura9tw92c">
              <OffersHeroBanner data-id="element-7462" data-mp="2d3pcsx42lypa" />
            </section>
            <section className="py-1 md:py-4" data-id="element-7463" data-mp="10vetlkaao0l2">
              <OffersBannerRow columns={6} bannerSpec={{
              width: 220,
              height: 220,
              ratio: '1:1'
            }} count={6} data-id="element-7464" data-mp="10y9efcx7znf0" />
            </section>
            <section className="py-1 md:py-4" data-id="element-7465" data-mp="m6r3htj7wj2i">
              <TopOffers data-id="element-7466" data-mp="h8h7xhtkzggd" />
            </section>
            <section className="py-1 md:py-4" data-id="element-7467" data-mp="h98rtvdtfpqt">
              <OffersBannerRow columns={3} bannerSpec={{
              width: 440,
              height: 248,
              ratio: '16:9'
            }} count={3} data-id="element-7468" data-mp="d6vkx3nc1lsg" />
            </section>
            <section id="flash-sales" className="py-1 md:py-4 scroll-mt-32 transition-all" data-id="element-7469" data-mp="3a954t9v5kxa">
              <Suspense fallback={<SectionSkeleton data-id="element-7471" data-mp="1xm7d6rwfsarl" />} data-id="element-7470" data-mp="1xmpgu9lfhr9j">
                <FlashSales data-id="element-7472" data-mp="1xn8o8wdgcx9b" />
              </Suspense>
            </section>
            <section className="py-1 md:py-4" data-id="element-7473" data-mp="25701o8ev4egu">
              <OffersBannerRow columns={3} bannerSpec={{
              width: 360,
              height: 270,
              ratio: '4:3'
            }} count={6} data-id="element-7474" data-mp="2545hmhzrgvny" />
            </section>
            <section className="py-1 md:py-4" data-id="element-7475" data-mp="2jtt8epabgbxf">
              <ShopByCategory data-id="element-7476" data-mp="1m7dqjmdz9c9c" />
            </section>
            <section className="py-1 md:py-4" data-id="element-7477" data-mp="1m8nqyi8aywj5">
              <OffersBannerRow columns={4} bannerSpec={{
              width: 320,
              height: 240,
              ratio: '4:3'
            }} count={4} data-id="element-7478" data-mp="1r6gxjcdipsjy" />
            </section>
            <section id="deal-of-day" className="py-1 md:py-4 scroll-mt-32 transition-all" data-id="element-7479" data-mp="2167pp7elvvgv">
              <Suspense fallback={<SectionSkeleton data-id="element-7481" data-mp="3cja88809mv3o" />} data-id="element-7480" data-mp="3cjs2iz3daz8y">
                <DealOfTheDay data-id="element-7482" data-mp="3ci8kp422cg52" />
              </Suspense>
            </section>
            <section className="py-1 md:py-4" data-id="element-7483" data-mp="1n01caj3wqckc">
              <OffersBannerRow columns={2} bannerSpec={{
              width: 680,
              height: 382,
              ratio: '16:9'
            }} count={2} data-id="element-7484" data-mp="1mx5ecus27nvw" />
            </section>
            <section id="trending-now" className="py-1 md:py-4 scroll-mt-32 transition-all" data-id="element-7485" data-mp="20pbmpuoz0qks">
              <Suspense fallback={<SectionSkeleton data-id="element-7487" data-mp="181piqlbtzjxe" />} data-id="element-7486" data-mp="142qhbapb65ap">
                <TrendingProducts data-id="element-7488" data-mp="1830p8swnv5s7" />
              </Suspense>
            </section>
            <section className="py-1 md:py-4" data-id="element-7489" data-mp="1d0spca7gly7h">
              <OffersFullWidthBanner data-id="element-7490" data-mp="1czr3rbsthtbi" />
            </section>
            <section id="exclusive-deals" className="py-1 md:py-4 scroll-mt-32 transition-all" data-id="element-7491" data-mp="1i16muybfppr9">
              <Suspense fallback={<SectionSkeleton data-id="element-7493" data-mp="2oilgzlqfs09m" />} data-id="element-7492" data-mp="2ofz3z6ks0sk0">
                <ExclusiveDeals data-id="element-7494" data-mp="2teiulkabesip" />
              </Suspense>
            </section>
            <section className="py-1 md:py-4" data-id="element-7495" data-mp="hrp3i0ijqcyp">
              <OffersBannerRow columns={6} bannerSpec={{
              width: 260,
              height: 346,
              ratio: '3:4'
            }} count={6} data-id="element-7496" data-mp="hrfeog98mbul" />
            </section>
            <section id="clearance-sale" className="py-1 md:py-4 scroll-mt-32 transition-all" data-id="element-7497" data-mp="33ua0hwps4kk">
              <Suspense fallback={<SectionSkeleton data-id="element-7499" data-mp="3v4s91twcty32" />} data-id="element-7498" data-mp="3v8fxwfqutwbg">
                <EnhancedClearanceSection data-id="element-7500" data-mp="3q78suog3jb8h" />
              </Suspense>
            </section>
          </main>
        </PullToRefresh>
        <Footer data-id="element-7501" data-mp="3l9gqt1lqm8qk" />
        <MobileFooterNav data-id="element-7502" data-mp="3l8xi8bhz8wp7" />
        <PharmacistChat data-id="element-7503" data-mp="3g9m0az5vjl61" />
        <div className="h-20 md:hidden" data-id="element-7504" data-mp="3gadvw9zh1iwb"></div>

        {/* Floating Apply Coupon CTA (Mobile Only) */}
        <button onClick={() => setCouponModalOpen(true)} className="md:hidden fixed bottom-[80px] left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 bg-gradient-to-r from-[#fdc300] to-[#ffd84d] text-[#1E2B55] rounded-full shadow-xl px-5 py-3 font-bold text-sm active:scale-95 transition-transform" aria-label="Apply a coupon" data-id="element-7505" data-mp="1jhkhlyn1layy">
          <TicketIcon className="w-4 h-4" data-id="element-7506" data-mp="2z0a1ffpggqne" />
          Apply Coupon
        </button>

        <LocationModal isOpen={locationModalOpen} onClose={() => setLocationModalOpen(false)} onSelectLocation={location => {
        setSelectedLocation(location);
        setLocationModalOpen(false);
      }} data-id="element-7507" data-mp="2yyfc7p75icdi" />
        <CouponModal isOpen={couponModalOpen} onClose={() => setCouponModalOpen(false)} onApplyCoupon={code => {
        toast.success(`Coupon ${code} copied! Apply at checkout.`);
        setCouponModalOpen(false);
      }} data-id="element-7508" data-mp="hbhhe7u75hz" />
      </div>
    </HelmetProvider>;
}